<template>
  <div
    class="sf-checkbox"
    :class="{
      'is-active': isChecked,
      'is-disabled': disabled,
      'has-error': !valid,
      'is-required': required,
    }"
    :data-testid="name"
  >
    <label class="sf-checkbox__container">
      <input
        v-focus
        type="checkbox"
        :name="name"
        :value="value"
        :checked="isChecked"
        :disabled="disabled"
        class="sf-checkbox__input"
        @change="inputHandler"
      >
      <slot
        name="checkmark"
        v-bind="{ isChecked, disabled }"
      >
        <span
          class="sf-checkbox__checkmark"
          :class="{ 'sf-checkbox__checkmark is-active': isChecked }"
        >
          <SfIcon
            :class="{ 'display-none': !isChecked }"
            icon="check"
            color="white"
          />
        </span>
      </slot>
      <slot
        name="label"
        v-bind="{ label, isChecked, disabled }"
      >
        <span
          :class="{ 'display-none': !label }"
          class="sf-checkbox__label"
        >{{
          label
        }}</span>
      </slot>
    </label>
    <div class="sf-checkbox__message">
      <transition name="sf-fade">
        <slot
          v-if="!disabled"
          :name="computedMessageSlotName"
          v-bind="{ computedMessage }"
        >
          <div :class="computedMessageClass">
            {{ computedMessage }}
          </div>
        </slot>
      </transition>
    </div>
  </div>
</template>
<script>

import { focus } from '@/directives/focus/focus';
export default {
  name: 'SfCheckbox',
  directives: {
    focus,
  },
  props: {
    modelValue: {
      type: [Array, Boolean],
      default: undefined,
    },
    name: {
      type: String,
      default: '',
    },
    value: {
      type: [String, Boolean],
      default: '',
    },
    label: {
      type: String,
      default: '',
    },
    hintMessage: {
      type: String,
      default: 'Required.',
    },
    required: {
      type: Boolean,
      default: false,
    },
    infoMessage: {
      type: String,
      default: '',
    },
    errorMessage: {
      type: String,
      default: 'This field is not correct.',
    },
    valid: {
      type: Boolean,
      default: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:modelValue'],
  computed: {
    isChecked() {

      if (typeof this.modelValue === 'boolean') {

        return this.modelValue;

      }

      return (this.modelValue || []).includes(this.value);
    
    },
    computedMessageSlotName() {

      return this.messagesHandler(
        'show-error-message',
        'show-info-message',
        this.required ? 'show-hint-message' : '',
      );
    
    },
    computedMessage() {

      return this.messagesHandler(
        this.errorMessage,
        this.infoMessage,
        this.required ? this.hintMessage : '',
      );
    
    },
    computedMessageClass() {

      return this.messagesHandler(
        'sf-checkbox__message--error',
        'sf-checkbox__message--info',
        this.required ? 'sf-checkbox__message--hint' : '',
      );
    
    },
  },
  methods: {
    inputHandler() {

      const selected = this.modelValue;

      if (typeof selected === 'boolean') {

        const next = !selected;
        this.$emit('update:modelValue', next);

        return;

      }

      let next = [...(selected || [])];

      if (next.includes(this.value)) {

        next = next.filter((value) => value !== this.value);

      } else {

        next.push(this.value);

      }

      this.$emit('update:modelValue', next);
    
    },
    messagesHandler(error, info, hint) {

      if (this.errorMessage && !this.valid) {

        return error;
      
      } else if (this.infoMessage && this.valid) {

        return info;
      
      } else if (this.hintMessage) {

        return hint;
      
      } else {

        return '';
      
      }
    
    },
  },
};

</script>
<style lang="scss">
@use "~/assets/scss/helpers/_layout" as layout;
@use "~/assets/scss/helpers/_typography" as typography;
.sf-checkbox {
  --icon-size: 1.25rem;

  &__container {
    position: relative;
    display: flex;
    align-items: center;
    padding: var(--checkbox-padding);
    width: var(--checkbox-container-width);
    cursor: var(--checkbox-cursor, pointer);
  }

  &__checkmark {
    box-sizing: border-box;
    display: var(--checkbox-display, flex);
    align-items: center;
    justify-content: center;
    order: var(--checkbox-order);
    width: var(--checkbox-size, 1.5rem);
    height: var(--checkbox-size, 1.5rem);

    @include layout.border(
      --checkbox-border,
      var(--checkbox-border-width, 1px),
      solid,
      var(--checkbox-border-color, var(--c-link))
    );

    border-radius: var(--checkbox-border-radius);
    background: var(--checkbox-background, transparent);
    transition: background 150ms linear, border-color 150ms linear;

    .is-disabled & {
      &.is-active {
        --checkbox-background: var(--c-text-disabled);
      }
    }

    .has-error & {
      &.is-active {
        --checkbox-background: var(--c-danger);
      }
    }
  }

  &__label {
    flex: 1;
    margin: var(--checkbox-label-margin, 0 0 0 var(--spacer-xs));
    color: var(--checkbox-label-color, var(--c-link));

    @include typography.font(
      --checkbox-font,
      var(--font-weight--normal),
      var(--font-size--base),
      1.6,
      var(--font-family--primary)
    );

    transition: color 150ms linear;

    &:hover {
      --checkbox-label-color: var(--c-black);

      .sf-checkbox--is-disabled & {
        --checkbox-label-color: var(--c-text-disabled);
      }
    }
  }

  &.is-active {
    --checkbox-background: var(--c-primary);

    &.has-error {
      --checkbox-background: var(--c-danger);
      --checkbox-message-color: var(--c-danger);
    }

    &.is-disabled {
      --checkbox-background: var(--c-text-disabled);
    }
  }

  &.is-required {
    --checkbox-message-color: var(--c-text);

    &.has-error {
      --checkbox-message-color: var(--c-danger);
    }
  }

  &.is-disabled {
    --checkbox-label-color: var(--c-text-disabled);
  }

  &__message {
    margin: var(--checkbox-message-margin, var(--spacer-2xs) 0 0 0);
    color: var(--checkbox-message-color, var(--c-danger));

    @include typography.font(
      --checkbox-message-font,
      var(--font-weight--normal),
      var(--font-size--xs),
      1.2,
      var(--font-family--secondary)
    );

    .sf-checkbox__message--info {
      color: var(--checkbox-message-color, var(--c-primary));
    }
  }

  input {
    position: absolute;
    opacity: 0;
    left: -1000%;
    width: 1px;
    height: 1px;

    &[style*="outline: none"]:focus + .sf-checkbox__checkmark {
      outline: none;
    }

    &:focus + .sf-checkbox__checkmark {
      outline-color: -webkit-focus-ring-color;
      outline-style: auto;
    }
  }
}
</style>
